import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Money, ArrowsClockwise, Trash, Receipt, Warning, Sparkle,
  Plus, FloppyDisk, Check, X, ListChecks, EnvelopeSimple, DownloadSimple,
  CopySimple, ArrowSquareOut, PaperPlaneTilt, Copy
} from "@phosphor-icons/react";
import { PageHeader } from "../components/ui/kaizen";

const fmt = (n) => Number(n || 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 2 });

async function downloadCsv(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`${filename} téléchargé`);
  } catch {
    toast.error("Erreur de téléchargement");
  }
}

const TABS = [
  { key: "duplicates", label: "Doublons WellyBox", icon: Copy, accent: "#D90429" },
  { key: "recoveries", label: "Récupérations", icon: ArrowsClockwise, accent: "#D90429" },
  { key: "subscriptions", label: "Kill-switch", icon: Trash, accent: "#0A0A0A" },
  { key: "anomalies", label: "Anomalies WellyBox", icon: Warning, accent: "#D90429" },
  { key: "accounting", label: "Notes Billy", icon: Receipt, accent: "#0A0A0A" },
];

const RECOVERY_STATUS = [
  ["a_contester", "À contester"],
  ["en_cours", "En cours"],
  ["recupere", "Récupéré"],
  ["abandonne", "Abandonné"],
];
const RECOVERY_CAT = [
  ["immofacile", "Immofacile"],
  ["zen_planner", "Zen Planner"],
  ["hotellerie", "Hôtellerie"],
  ["medias", "Médias"],
  ["autre", "Autre"],
];
const SUB_STATUS = [["actif", "Actif"], ["a_resilier", "À résilier"], ["resilie", "Résilié"]];
const SUB_CAT = [
  ["logiciel", "Logiciel"],
  ["cybersecurite", "Cybersécurité"],
  ["app_mobile", "App mobile"],
  ["service", "Service"],
  ["telecom", "Télécom"],
  ["autre", "Autre"],
];
const ANO_STATUS = [["a_traiter", "À traiter"], ["traite", "Traité"]];
const ACC_TREATMENT = [
  ["dpa_amortissement", "DPA / Amortissement"],
  ["recup_tps_tvq", "Récup. TPS/TVQ"],
  ["capitalisation", "Capitalisation"],
  ["recu_familial", "Reçu familial"],
  ["autre", "Autre"],
];
const ACC_STATUS = [["a_traiter", "À traiter"], ["transmis", "Transmis"], ["traite", "Traité"]];

export default function RedressementModule() {
  const [tab, setTab] = useState("recoveries");
  const [summary, setSummary] = useState(null);

  const reloadSummary = () => api.get("/redressement/summary").then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { reloadSummary(); }, []);

  return (
    <div data-testid="redressement-page" className="bg-background text-foreground min-h-screen">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<>Redressement financier</>} subtitle={<>Récupérez l'argent qui vous est dû, résiliez les abonnements inutiles et corrigez les anomalies comptables — un seul écran, action directe.</>} />
      </div>

      {/* KPIs globaux */}
      {summary && (
        <div className="hard-border-b grid grid-cols-2 lg:grid-cols-4 gap-0" data-testid="redressement-kpis">
          <Kpi label="À récupérer (en attente)" value={fmt(summary.recoveries.potentiel_pending)} sub={`${summary.recoveries.count} dossiers`} accent="#D90429"/>
          <Kpi label="Économies annuelles potentielles" value={fmt(summary.subscriptions.economies_annuelles_potentielles)} sub={`${fmt(summary.subscriptions.a_resilier_monthly)}/mois × 12`} accent="#0A0A0A"/>
          <Kpi label="Anomalies à purger" value={fmt(summary.anomalies.a_traiter_total)} sub={`${summary.anomalies.count} lignes WellyBox`} accent="#D90429"/>
          <Kpi label="Notes Billy à transmettre" value={fmt(summary.accounting.a_traiter)} sub={`${summary.accounting.count} dossiers`} accent="#0A0A0A"/>
        </div>
      )}

      {/* Tabs */}
      <div className="hard-border-b flex overflow-x-auto" data-testid="redressement-tabs">
        {TABS.map((t) => {
          const I = t.icon;
          const active = t.key === tab;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-6 py-4 ticker border-r border-border transition-colors whitespace-nowrap ${active ? 'bg-foreground text-white' : 'text-muted-foreground hover:bg-secondary/40'}`}>
              <I size={16}/> {t.label}
            </button>
          );
        })}
      </div>

      <div className="px-8 lg:px-12 py-8">
        {tab === "duplicates" && <DuplicatesPanel onChange={reloadSummary}/>}
        {tab === "recoveries" && <RecoveriesPanel onChange={reloadSummary}/>}
        {tab === "subscriptions" && <SubscriptionsPanel onChange={reloadSummary}/>}
        {tab === "anomalies" && <AnomaliesPanel onChange={reloadSummary}/>}
        {tab === "accounting" && <AccountingPanel onChange={reloadSummary}/>}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className="p-6 border-r border-border last:border-r-0" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="ticker text-muted-foreground">{label}</div>
      <div className="font-display font-black text-3xl mt-2" style={{ color: accent }}>{value}</div>
      <div className="ticker text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

/* ====================== RECOVERIES ====================== */
function RecoveriesPanel({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor: "", category: "autre", amount_potential: 0, charges_count: 1, status: "a_contester", notes: "" });
  const [letter, setLetter] = useState(null); // { recovery_id, vendor, subject, body, generated_at }
  const [letterBusyId, setLetterBusyId] = useState(null);

  const load = () => api.get("/redressement/recoveries").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/redressement/recoveries", form);
      setForm({ vendor: "", category: "autre", amount_potential: 0, charges_count: 1, status: "a_contester", notes: "" });
      setOpen(false);
      await load(); onChange();
      toast.success("Récupération ajoutée");
    } catch { toast.error("Erreur"); }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/redressement/recoveries/${id}`, patch);
      await load(); onChange();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette récupération ?")) return;
    try { await api.delete(`/redressement/recoveries/${id}`); await load(); onChange(); toast.success("Supprimée"); } catch { toast.error("Erreur"); }
  };

  const draftLetter = async (recovery, force = false) => {
    // Si un draft est déjà en cache et qu'on ne force pas, ouvre directement le modal
    if (!force && recovery.last_letter_subject && recovery.last_letter_body) {
      setLetter({
        recovery_id: recovery.id,
        vendor: recovery.vendor,
        subject: recovery.last_letter_subject,
        body: recovery.last_letter_body,
        generated_at: recovery.last_letter_generated_at,
        cached: true,
      });
      return;
    }
    setLetterBusyId(recovery.id);
    try {
      const res = await api.post(`/redressement/recoveries/${recovery.id}/draft-letter`);
      setLetter(res.data);
      await load(); // recharge pour récupérer les last_letter_* persistés
      toast.success("Courriel généré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de génération");
    } finally {
      setLetterBusyId(null);
    }
  };

  return (
    <div data-testid="recoveries-panel">
      <SectionHeader title="Récupérations à contester" subtitle="Doublons, charges erronées, frais à réclamer">
        <div className="flex items-center gap-2">
          <button onClick={() => downloadCsv("/exports/recoveries.csv", "recuperations.csv")}
            data-testid="export-recoveries-csv"
            className="hard-border px-4 py-2.5 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors">
            <DownloadSimple size={16}/> CSV
          </button>
          <button onClick={() => setOpen(!open)} data-testid="add-recovery-btn"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors">
            <Plus size={16}/> {open ? "Annuler" : "Ajouter"}
          </button>
        </div>
      </SectionHeader>

      {open && (
        <form onSubmit={create} className="hard-border p-5 grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 bg-secondary/30" data-testid="recovery-form">
          <input required placeholder="Fournisseur" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="hard-border px-3 py-2" data-testid="rec-vendor"/>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="rec-category">
            {RECOVERY_CAT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="Montant à récupérer ($)" value={form.amount_potential} onChange={(e) => setForm({ ...form, amount_potential: parseFloat(e.target.value) || 0 })} className="hard-border px-3 py-2" data-testid="rec-amount"/>
          <input type="number" placeholder="Nb de charges" value={form.charges_count} onChange={(e) => setForm({ ...form, charges_count: parseInt(e.target.value) || 1 })} className="hard-border px-3 py-2" data-testid="rec-charges"/>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="rec-status">
            {RECOVERY_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="hard-border px-3 py-2 md:col-span-3" data-testid="rec-notes"/>
          <button className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-3 transition-colors" data-testid="rec-submit">
            <Plus size={14} className="inline mr-1"/> Créer la récupération
          </button>
        </form>
      )}

      {list.length === 0 ? (
        <EmptyState icon={ArrowsClockwise} title="Aucune récupération" subtitle="Ajoute les charges erronées et doublons à réclamer."/>
      ) : (
        <div className="hard-border" data-testid="recoveries-list">
          {list.map((r, i) => (
            <div key={r.id} className={`p-5 ${i < list.length - 1 ? 'border-b border-border' : ''} grid grid-cols-1 md:grid-cols-12 gap-4 items-center`} data-testid={`recovery-${i}`}>
              <div className="md:col-span-4">
                <div className="font-display font-bold text-lg">{r.vendor}</div>
                <div className="ticker text-muted-foreground">{RECOVERY_CAT.find(([v]) => v === r.category)?.[1] || r.category}{r.charges_count > 1 ? ` · ${r.charges_count} charges` : ''}</div>
                {r.notes && <div className="text-xs text-muted-foreground mt-2">{r.notes}</div>}
              </div>
              <div className="md:col-span-3 text-right">
                <div className="font-display font-black text-2xl text-[#D90429]">{fmt(r.amount_potential)}</div>
                {r.amount_recovered > 0 && <div className="ticker text-muted-foreground">Récupéré: {fmt(r.amount_recovered)}</div>}
              </div>
              <div className="md:col-span-2">
                <select value={r.status} onChange={(e) => update(r.id, { status: e.target.value })} className="hard-border px-3 py-1.5 bg-white text-sm w-full" data-testid={`recovery-status-${i}`}>
                  {RECOVERY_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="md:col-span-3 flex items-center justify-end gap-2">
                <button onClick={() => draftLetter(r)} disabled={letterBusyId === r.id}
                  data-testid={`recovery-draft-btn-${i}`}
                  title={r.last_letter_subject ? "Courriel en cache — clic pour le voir" : "Générer un courriel de contestation IA"}
                  className="bg-foreground hover:bg-[#D90429] text-white px-3 py-1.5 ticker flex items-center gap-1 transition-colors disabled:opacity-50">
                  <EnvelopeSimple size={14}/>
                  {letterBusyId === r.id ? "…" : (r.last_letter_subject ? "Voir contestation" : "Contestation IA")}
                </button>
                <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-[#D90429] p-2" data-testid={`recovery-delete-${i}`}><Trash size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {letter && (
        <ContestationModal
          letter={letter}
          regenerating={letterBusyId === letter.recovery_id}
          onClose={() => setLetter(null)}
          onRegenerate={async () => {
            const target = list.find((x) => x.id === letter.recovery_id);
            if (target) await draftLetter(target, true);
          }}
        />
      )}
    </div>
  );
}

function ContestationModal({ letter, onClose, onRegenerate, regenerating }) {
  const [gmailStatus, setGmailStatus] = useState(null);
  const [showSendForm, setShowSendForm] = useState(false);
  const [sendForm, setSendForm] = useState({ to: "", cc: "", sending: false });

  useEffect(() => {
    api.get("/oauth/gmail/status").then((r) => setGmailStatus(r.data)).catch(() => {});
  }, []);

  const fullText = `Objet : ${letter.subject}\n\n${letter.body}`;
  const copyAll = async () => {
    try { await navigator.clipboard.writeText(fullText); toast.success("Copié dans le presse-papiers"); }
    catch { toast.error("Impossible de copier"); }
  };
  const mailto = `mailto:?subject=${encodeURIComponent(letter.subject)}&body=${encodeURIComponent(letter.body)}`;

  const sendViaGmail = async () => {
    if (!sendForm.to) { toast.error("Destinataire requis"); return; }
    setSendForm((s) => ({ ...s, sending: true }));
    try {
      const res = await api.post(`/redressement/recoveries/${letter.recovery_id}/send-letter`, {
        to: sendForm.to,
        cc: sendForm.cc || undefined,
      });
      toast.success(`Envoyé à ${res.data.to}`);
      setShowSendForm(false);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSendForm((s) => ({ ...s, sending: false }));
    }
  };

  const gmailReady = gmailStatus?.connected;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="contestation-modal">
      <div className="bg-background text-foreground hard-border max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="hard-border-b p-5 flex items-start justify-between gap-4">
          <div>
            <div className="ticker text-muted-foreground mb-1">
              Courriel de contestation IA · {letter.vendor}
              {letter.cached && <span className="ml-2 text-[#D90429]">· en cache</span>}
            </div>
            <h3 className="font-display font-black text-xl tracking-tight">{letter.subject}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-2" data-testid="modal-close"><X size={20}/></button>
        </div>
        <div className="p-5 overflow-auto flex-1">
          <pre className="whitespace-pre-wrap font-[Satoshi] text-sm leading-relaxed" data-testid="contestation-body">{letter.body}</pre>

          {showSendForm && (
            <div className="mt-5 hard-border p-4 bg-secondary/30 space-y-3" data-testid="gmail-send-form">
              <div className="ticker text-muted-foreground">Envoi via Gmail · Compte : {gmailStatus?.google_email}</div>
              <input type="email" required placeholder="Destinataire (ex: facturation@immofacile.ca)"
                value={sendForm.to} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                className="w-full hard-border px-3 py-2" data-testid="gmail-send-to"/>
              <input type="email" placeholder="CC (optionnel)"
                value={sendForm.cc} onChange={(e) => setSendForm({ ...sendForm, cc: e.target.value })}
                className="w-full hard-border px-3 py-2" data-testid="gmail-send-cc"/>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setShowSendForm(false)} className="hard-border px-4 py-2 ticker hover:bg-foreground hover:text-white transition-colors">Annuler</button>
                <button onClick={sendViaGmail} disabled={sendForm.sending} data-testid="gmail-send-confirm"
                  className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2 ticker flex items-center gap-2 transition-colors disabled:opacity-50">
                  <ArrowSquareOut size={14}/> {sendForm.sending ? "Envoi…" : "Envoyer maintenant"}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="hard-border-t p-4 flex items-center justify-between gap-2 bg-secondary/30 flex-wrap">
          <button onClick={onRegenerate} disabled={regenerating} data-testid="regenerate-letter-btn"
            className="hard-border px-3 py-2 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors disabled:opacity-50">
            <Sparkle size={14}/> {regenerating ? "Régénération…" : "Régénérer avec l'IA"}
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={copyAll} data-testid="copy-letter-btn"
              className="hard-border px-4 py-2 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors">
              <CopySimple size={14}/> Copier tout
            </button>
            <a href={mailto} target="_blank" rel="noreferrer" data-testid="open-mailto-btn"
              className="hard-border px-4 py-2 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors">
              <ArrowSquareOut size={14}/> Client mail
            </a>
            {gmailReady ? (
              <button onClick={() => setShowSendForm(!showSendForm)} data-testid="send-via-gmail-btn"
                className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2 ticker flex items-center gap-2 transition-colors">
                <PaperPlaneTilt size={14}/> Envoyer via Gmail
              </button>
            ) : gmailStatus?.configured === false ? (
              <span className="text-xs text-muted-foreground">Gmail non configuré</span>
            ) : (
              <Link to="/app/integrations" data-testid="connect-gmail-link"
                className="bg-foreground hover:bg-[#D90429] text-white px-5 py-2 ticker flex items-center gap-2 transition-colors">
                <PaperPlaneTilt size={14}/> Connecter Gmail
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ====================== SUBSCRIPTIONS ====================== */
function SubscriptionsPanel({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "logiciel", monthly_cost: 0, status: "actif", notes: "", action_date: "" });

  const load = () => api.get("/redressement/subscriptions").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.action_date) delete payload.action_date;
      await api.post("/redressement/subscriptions", payload);
      setForm({ name: "", category: "logiciel", monthly_cost: 0, status: "actif", notes: "", action_date: "" });
      setOpen(false); await load(); onChange();
      toast.success("Abonnement ajouté");
    } catch { toast.error("Erreur"); }
  };

  const update = async (id, patch) => {
    try { await api.patch(`/redressement/subscriptions/${id}`, patch); await load(); onChange(); }
    catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet abonnement ?")) return;
    try { await api.delete(`/redressement/subscriptions/${id}`); await load(); onChange(); toast.success("Supprimé"); } catch { toast.error("Erreur"); }
  };

  const totalAResilier = list.filter((s) => s.status === "a_resilier").reduce((s, x) => s + (x.monthly_cost || 0), 0);

  return (
    <div data-testid="subscriptions-panel">
      <SectionHeader title="Kill-switch des abonnements" subtitle={`${fmt(totalAResilier)}/mois prêts à être coupés · ${fmt(totalAResilier * 12)}/an d'économies`}>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadCsv("/exports/subscriptions.csv", "abonnements.csv")}
            data-testid="export-subscriptions-csv"
            className="hard-border px-4 py-2.5 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors">
            <DownloadSimple size={16}/> CSV
          </button>
          <button onClick={() => setOpen(!open)} data-testid="add-subscription-btn"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors">
            <Plus size={16}/> {open ? "Annuler" : "Ajouter"}
          </button>
        </div>
      </SectionHeader>

      {open && (
        <form onSubmit={create} className="hard-border p-5 grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 bg-secondary/30" data-testid="sub-form">
          <input required placeholder="Nom du service" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="hard-border px-3 py-2" data-testid="sub-name"/>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="sub-category">
            {SUB_CAT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="number" step="0.01" placeholder="Coût mensuel ($)" value={form.monthly_cost} onChange={(e) => setForm({ ...form, monthly_cost: parseFloat(e.target.value) || 0 })} className="hard-border px-3 py-2" data-testid="sub-cost"/>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="sub-status">
            {SUB_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input type="date" value={form.action_date} onChange={(e) => setForm({ ...form, action_date: e.target.value })} className="hard-border px-3 py-2" data-testid="sub-action-date"/>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="hard-border px-3 py-2" data-testid="sub-notes"/>
          <button className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-3 transition-colors" data-testid="sub-submit">Créer l'abonnement</button>
        </form>
      )}

      {list.length === 0 ? (
        <EmptyState icon={Trash} title="Aucun abonnement listé" subtitle="Ajoute les services à résilier ou consolider."/>
      ) : (
        <div className="hard-border" data-testid="subscriptions-list">
          {list.map((s, i) => (
            <div key={s.id} className={`p-4 ${i < list.length - 1 ? 'border-b border-border' : ''} grid grid-cols-1 md:grid-cols-12 gap-3 items-center`} data-testid={`subscription-${i}`}>
              <div className="md:col-span-4">
                <div className="font-display font-bold">{s.name}</div>
                <div className="ticker text-muted-foreground">{SUB_CAT.find(([v]) => v === s.category)?.[1]}</div>
                {s.notes && <div className="text-xs text-muted-foreground mt-1">{s.notes}</div>}
              </div>
              <div className="md:col-span-2 text-right">
                <div className="font-mono font-bold">{fmt(s.monthly_cost)}/mois</div>
                <div className="ticker text-muted-foreground">{fmt((s.monthly_cost || 0) * 12)}/an</div>
              </div>
              <div className="md:col-span-3">
                <select value={s.status} onChange={(e) => update(s.id, { status: e.target.value })}
                  className={`hard-border px-3 py-1.5 text-sm w-full font-semibold ${s.status === 'a_resilier' ? 'bg-[#D90429] text-white border-[#D90429]' : s.status === 'resilie' ? 'bg-foreground text-white border-foreground' : 'bg-white'}`}
                  data-testid={`sub-status-${i}`}>
                  {SUB_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 ticker text-muted-foreground text-xs">
                {s.action_date && <>Action: {s.action_date}</>}
              </div>
              <div className="md:col-span-1 flex justify-end">
                <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-[#D90429] p-2" data-testid={`sub-delete-${i}`}><Trash size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ====================== ANOMALIES ====================== */
function AnomaliesPanel({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ source: "wellybox", vendor: "", amount: 0, type: "purge", notes: "", status: "a_traiter" });

  const load = () => api.get("/redressement/anomalies").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/redressement/anomalies", form);
      setForm({ source: "wellybox", vendor: "", amount: 0, type: "purge", notes: "", status: "a_traiter" });
      setOpen(false); await load(); onChange();
      toast.success("Anomalie ajoutée");
    } catch { toast.error("Erreur"); }
  };

  const toggleStatus = async (id, current) => {
    const newStatus = current === "a_traiter" ? "traite" : "a_traiter";
    try { await api.patch(`/redressement/anomalies/${id}`, { status: newStatus }); await load(); onChange(); }
    catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette anomalie ?")) return;
    try { await api.delete(`/redressement/anomalies/${id}`); await load(); onChange(); toast.success("Supprimée"); } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="anomalies-panel">
      <SectionHeader title="Anomalies WellyBox à purger" subtitle="Lignes aberrantes à nettoyer du bilan">
        <div className="flex items-center gap-2">
          <button onClick={() => downloadCsv("/exports/anomalies.csv", "anomalies.csv")}
            data-testid="export-anomalies-csv"
            className="hard-border px-4 py-2.5 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors">
            <DownloadSimple size={16}/> CSV
          </button>
          <button onClick={() => setOpen(!open)} data-testid="add-anomaly-btn"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors">
            <Plus size={16}/> {open ? "Annuler" : "Ajouter"}
          </button>
        </div>
      </SectionHeader>

      {open && (
        <form onSubmit={create} className="hard-border p-5 grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 bg-secondary/30" data-testid="ano-form">
          <input placeholder="Source (wellybox / quickbooks)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="hard-border px-3 py-2" data-testid="ano-source"/>
          <input required placeholder="Fournisseur / Ligne" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="hard-border px-3 py-2" data-testid="ano-vendor"/>
          <input type="number" step="0.01" placeholder="Montant ($)" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="hard-border px-3 py-2" data-testid="ano-amount"/>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="ano-type">
            <option value="purge">Purge</option>
            <option value="correction">Correction</option>
          </select>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="hard-border px-3 py-2 md:col-span-2" data-testid="ano-notes"/>
          <button className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-3 transition-colors" data-testid="ano-submit">Créer l'anomalie</button>
        </form>
      )}

      {list.length === 0 ? (
        <EmptyState icon={Warning} title="Aucune anomalie" subtitle="Ajoute les lignes aberrantes à nettoyer."/>
      ) : (
        <div className="hard-border" data-testid="anomalies-list">
          {list.map((a, i) => (
            <div key={a.id} className={`p-5 ${i < list.length - 1 ? 'border-b border-border' : ''} grid grid-cols-1 md:grid-cols-12 gap-3 items-center ${a.status === 'traite' ? 'opacity-50' : ''}`} data-testid={`anomaly-${i}`}>
              <div className="md:col-span-5">
                <div className="font-display font-bold text-lg">{a.vendor}</div>
                <div className="ticker text-muted-foreground">{a.source} · {a.type}</div>
                {a.notes && <div className="text-xs text-muted-foreground mt-1">{a.notes}</div>}
              </div>
              <div className="md:col-span-4 text-right">
                <div className="font-display font-black text-2xl text-[#D90429]">{fmt(a.amount)}</div>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button onClick={() => toggleStatus(a.id, a.status)}
                  className={`hard-border px-3 py-1.5 ticker flex items-center gap-1 transition-colors ${a.status === 'traite' ? 'bg-foreground text-white border-foreground' : 'hover:bg-foreground hover:text-white'}`}
                  data-testid={`anomaly-toggle-${i}`}>
                  {a.status === 'traite' ? <><Check size={14}/> Traité</> : <><X size={14}/> À traiter</>}
                </button>
              </div>
              <div className="md:col-span-1 flex justify-end">
                <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-[#D90429] p-2" data-testid={`anomaly-delete-${i}`}><Trash size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ====================== ACCOUNTING (BILLY) ====================== */
function AccountingPanel({ onChange }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor: "", amount: 0, treatment: "autre", accountant: "billy", notes: "", status: "a_traiter" });

  const load = () => api.get("/redressement/accounting").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/redressement/accounting", form);
      setForm({ vendor: "", amount: 0, treatment: "autre", accountant: "billy", notes: "", status: "a_traiter" });
      setOpen(false); await load(); onChange();
      toast.success("Note ajoutée");
    } catch { toast.error("Erreur"); }
  };

  const update = async (id, patch) => {
    try { await api.patch(`/redressement/accounting/${id}`, patch); await load(); onChange(); }
    catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try { await api.delete(`/redressement/accounting/${id}`); await load(); onChange(); toast.success("Supprimée"); } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="accounting-panel">
      <SectionHeader title="Notes pour Billy (comptable)" subtitle="Directives DPA, récupérations TPS/TVQ, capitalisations, reçus familiaux">
        <div className="flex items-center gap-2">
          <button onClick={() => downloadCsv("/exports/accounting-notes.csv", "notes_billy.csv")}
            data-testid="export-accounting-csv"
            className="hard-border px-4 py-2.5 ticker flex items-center gap-2 hover:bg-foreground hover:text-white transition-colors">
            <DownloadSimple size={16}/> CSV
          </button>
          <button onClick={() => setOpen(!open)} data-testid="add-accounting-btn"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors">
            <Plus size={16}/> {open ? "Annuler" : "Ajouter"}
          </button>
        </div>
      </SectionHeader>

      {open && (
        <form onSubmit={create} className="hard-border p-5 grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 bg-secondary/30" data-testid="acc-form">
          <input required placeholder="Fournisseur / source" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="hard-border px-3 py-2" data-testid="acc-vendor"/>
          <input type="number" step="0.01" placeholder="Montant ($)" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="hard-border px-3 py-2" data-testid="acc-amount"/>
          <select value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="acc-treatment">
            {ACC_TREATMENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Comptable" value={form.accountant} onChange={(e) => setForm({ ...form, accountant: e.target.value })} className="hard-border px-3 py-2" data-testid="acc-accountant"/>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="hard-border px-3 py-2 bg-white" data-testid="acc-status">
            {ACC_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="hard-border px-3 py-2 md:col-span-3" data-testid="acc-notes"/>
          <button className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker md:col-span-3 transition-colors" data-testid="acc-submit">Créer la note</button>
        </form>
      )}

      {list.length === 0 ? (
        <EmptyState icon={Receipt} title="Aucune note pour Billy" subtitle="Centralise les directives comptables."/>
      ) : (
        <div className="hard-border" data-testid="accounting-list">
          {list.map((n, i) => (
            <div key={n.id} className={`p-5 ${i < list.length - 1 ? 'border-b border-border' : ''} grid grid-cols-1 md:grid-cols-12 gap-3 items-center`} data-testid={`accounting-${i}`}>
              <div className="md:col-span-4">
                <div className="font-display font-bold">{n.vendor}</div>
                <div className="ticker text-muted-foreground">{ACC_TREATMENT.find(([v]) => v === n.treatment)?.[1]} · {n.accountant}</div>
                {n.notes && <div className="text-xs text-muted-foreground mt-1">{n.notes}</div>}
              </div>
              <div className="md:col-span-3 text-right">
                <div className="font-display font-black text-xl">{n.amount > 0 ? fmt(n.amount) : "—"}</div>
              </div>
              <div className="md:col-span-3">
                <select value={n.status} onChange={(e) => update(n.id, { status: e.target.value })} className="hard-border px-3 py-1.5 bg-white text-sm w-full" data-testid={`acc-status-${i}`}>
                  {ACC_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button onClick={() => remove(n.id)} className="text-muted-foreground hover:text-[#D90429] p-2" data-testid={`acc-delete-${i}`}><Trash size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ====================== HELPERS ====================== */
function SectionHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
      <div>
        <h2 className="font-display font-black text-2xl tracking-tight">{title}</h2>
        {subtitle && <div className="ticker text-muted-foreground mt-1">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="hard-border p-12 text-center">
      <Icon size={48} weight="duotone" className="mx-auto text-muted-foreground mb-3"/>
      <div className="font-display text-xl font-bold">{title}</div>
      <div className="text-sm text-muted-foreground mt-1">{subtitle}</div>
    </div>
  );
}


// ==================== DUPLICATES PANEL (WellyBox dynamique) ====================

function DuplicatesPanel({ onChange }) {
  const [data, setData] = useState({ groups: [], count: 0, total_potential_recovery: 0 });
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [minAmount, setMinAmount] = useState(100);
  const [letter, setLetter] = useState(null);
  const [batchLetters, setBatchLetters] = useState(null);
  const [busy, setBusy] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchCount, setBatchCount] = useState(10);
  const [gmailStatus, setGmailStatus] = useState({ connected: false });
  const [sendingIdx, setSendingIdx] = useState(null);
  const [emailDraft, setEmailDraft] = useState({});  // { [group_id]: "email@vendor.com" }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/redressement/duplicates?min_amount=${minAmount}&resolved=${showResolved}`);
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement des doublons");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [showResolved, minAmount]);

  useEffect(() => {
    api.get("/oauth/gmail/status").then(r => setGmailStatus(r.data)).catch(() => {});
  }, []);

  const draftLetter = async (g) => {
    setBusy(g.group_id);
    try {
      const r = await api.post(`/redressement/duplicates/${g.group_id}/draft-letter`);
      setLetter(r.data);
      toast.success("Courriel de contestation généré");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur génération");
    } finally { setBusy(null); }
  };

  const resolve = async (g, status) => {
    setBusy(g.group_id);
    try {
      await api.post(`/redressement/duplicates/${g.group_id}/resolve`, { status, notes: "" });
      toast.success(status === "dismissed" ? "Groupe rejeté (faux positif)" : status === "resolved" ? "Récupération confirmée" : "Contestation envoyée");
      await load();
      onChange?.();
    } catch {
      toast.error("Erreur");
    } finally { setBusy(null); }
  };

  const copyLetter = () => {
    if (!letter) return;
    navigator.clipboard.writeText(`Objet : ${letter.subject}\n\n${letter.body}`);
    toast.success("Courriel copié dans le presse-papier");
  };

  const doBatch = async () => {
    if (!window.confirm(`Générer ${batchCount} courriels Claude en parallèle ? (30-60 secondes)`)) return;
    setBatchBusy(true);
    try {
      const r = await api.post("/redressement/duplicates/batch-draft", { count: batchCount, min_amount: minAmount });
      setBatchLetters(r.data);
      toast.success(`${r.data.generated} courriels générés · ${r.data.total_potential.toLocaleString("fr-CA")} $ couverts`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur batch");
    } finally { setBatchBusy(false); }
  };

  const copyBatchLetter = (lt) => {
    navigator.clipboard.writeText(`À: <service client ${lt.vendor}>\nObjet : ${lt.subject}\n\n${lt.body}`);
    toast.success(`${lt.vendor} copié`);
  };

  const copyAllBatchLetters = () => {
    if (!batchLetters) return;
    const all = batchLetters.letters.map(lt => `══════════════════════════════════════════\nFOURNISSEUR : ${lt.vendor}\nMONTANT     : ${lt.potential_recovery} $\nObjet : ${lt.subject}\n\n${lt.body}`).join("\n\n\n");
    navigator.clipboard.writeText(all);
    toast.success(`Les ${batchLetters.letters.length} courriels copiés dans le presse-papier`);
  };

  const sendLetterViaGmail = async (lt, idx) => {
    const to = (emailDraft[lt.group_id] || lt.vendor_email || "").trim();
    if (!to || !to.includes("@")) {
      toast.error(`Renseigne une adresse email pour ${lt.vendor}`);
      return;
    }
    if (!window.confirm(`Envoyer ce courriel à ${to} via Gmail ?`)) return;
    setSendingIdx(idx);
    try {
      await api.post(`/redressement/duplicates/${lt.group_id}/send-letter`, { to });
      // Persist vendor contact
      try { await api.post("/vendor-contacts", { vendor_name: lt.vendor, email_facturation: to }); } catch { /* non-fatal */ }
      toast.success(`Envoyé à ${to}`);
      // Mark as sent in UI
      setBatchLetters(prev => prev ? {
        ...prev,
        letters: prev.letters.map(l => l.group_id === lt.group_id ? { ...l, _sent: true, _sent_to: to } : l),
      } : prev);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec envoi Gmail");
    } finally { setSendingIdx(null); }
  };

  const sendAllBatchViaGmail = async () => {
    if (!batchLetters) return;
    const ready = batchLetters.letters.filter(lt => !lt._sent && (emailDraft[lt.group_id] || lt.vendor_email));
    if (ready.length === 0) {
      toast.error("Aucun courriel n'a d'adresse email renseignée"); return;
    }
    if (!window.confirm(`Envoyer ${ready.length} courriels via Gmail ? Action irréversible.`)) return;
    for (let i = 0; i < ready.length; i++) {
      const lt = ready[i];
      const idx = batchLetters.letters.indexOf(lt);
      await sendLetterViaGmail(lt, idx);
    }
  };

  return (
    <div className="space-y-6" data-testid="duplicates-panel">
      <div className="hard-border p-5 bg-secondary/30 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="ticker text-muted-foreground">Détection automatique via vos transactions WellyBox</div>
          <div className="font-display font-black text-2xl mt-1">
            {fmt(data.total_potential_recovery)} <span className="text-sm font-normal text-muted-foreground">potentiel total sur {data.count} groupes</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <label className="flex items-center gap-2 ticker">
            Seuil min
            <input type="number" min="0" value={minAmount} onChange={(e) => setMinAmount(Number(e.target.value) || 0)} className="hard-border px-2 py-1 w-24 font-mono" data-testid="dup-min-amount"/>
            $
          </label>
          <label className="flex items-center gap-2 ticker">
            <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} data-testid="dup-toggle-resolved"/>
            Afficher résolus
          </label>
          <div className="flex items-center gap-2">
            <input type="number" min="1" max="25" value={batchCount} onChange={(e) => setBatchCount(Number(e.target.value) || 10)} className="hard-border px-2 py-1 w-16 font-mono" data-testid="batch-count"/>
            <button onClick={doBatch} disabled={batchBusy || data.groups.length === 0} className="bg-[#D90429] hover:bg-[#BF0424] disabled:opacity-50 text-white px-4 py-2 ticker flex items-center gap-2 transition-colors" data-testid="batch-draft-btn">
              <Sparkle size={14}/> {batchBusy ? `Génération…` : `Batch TOP ${batchCount}`}
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Chargement…</div>}

      {!loading && data.groups.length === 0 && (
        <EmptyState icon={Check} title={showResolved ? "Aucun groupe résolu" : "Aucun doublon détecté"} subtitle="Augmente le seuil minimum ou consulte les résolus." />
      )}

      {!loading && data.groups.length > 0 && (
        <div className="hard-border" data-testid="duplicates-list">
          {data.groups.map(g => (
            <div key={g.group_id} className="p-4 border-b border-border last:border-0 flex items-start justify-between gap-4 flex-wrap" data-testid={`dup-${g.group_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display font-bold text-lg">{g.vendor}</span>
                  <span className="ticker bg-[#D90429] text-white px-2 py-0.5">×{g.occurrences}</span>
                  <span className="ticker text-muted-foreground">{g.date}</span>
                  <span className="ticker bg-secondary px-2 py-0.5">{g.category}</span>
                  {g.resolved && <span className="ticker bg-[#2A9D8F] text-white px-2 py-0.5">résolu</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {g.occurrences} charges de {fmt(g.amount)} le {g.date} · {g.account}
                  {g.doc_numbers?.length > 0 && <span className="ml-2 font-mono text-xs">docs: {g.doc_numbers.slice(0,3).join(", ")}</span>}
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <div className="text-right">
                  <div className="ticker text-muted-foreground">Récup potentielle</div>
                  <div className="font-mono font-bold text-[#D90429] text-lg">{fmt(g.potential_recovery)}</div>
                </div>
                {!g.resolved && (
                  <>
                    <button onClick={() => draftLetter(g)} disabled={busy === g.group_id} className="ticker hover:text-[#D90429] flex items-center gap-1.5 transition-colors disabled:opacity-50" title="Générer courriel Claude">
                      <Sparkle size={16}/> {busy === g.group_id ? "…" : "Courriel"}
                    </button>
                    <button onClick={() => resolve(g, "resolved")} disabled={busy === g.group_id} className="ticker hover:text-[#2A9D8F] flex items-center gap-1.5 transition-colors disabled:opacity-50" title="Marquer récupéré">
                      <Check size={16}/> Récupéré
                    </button>
                    <button onClick={() => resolve(g, "dismissed")} disabled={busy === g.group_id} className="ticker hover:text-muted-foreground flex items-center gap-1.5 transition-colors disabled:opacity-50" title="Faux positif">
                      <X size={16}/> Rejeter
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {letter && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setLetter(null)} data-testid="letter-modal">
          <div className="bg-background text-foreground hard-border max-w-2xl w-full my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="hard-border-b px-5 py-4 flex items-center justify-between">
              <div>
                <div className="ticker text-muted-foreground">Courriel de contestation · {letter.vendor}</div>
                <div className="font-display font-bold text-lg">{letter.subject}</div>
              </div>
              <button onClick={() => setLetter(null)} className="hover:text-[#D90429]"><X size={20}/></button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{letter.body}</pre>
            </div>
            <div className="hard-border-t p-5 flex items-center justify-between gap-3">
              <button onClick={() => setLetter(null)} className="ticker hover:text-muted-foreground">Fermer</button>
              <div className="flex items-center gap-3">
                <button onClick={copyLetter} className="ticker hover:text-[#D90429] flex items-center gap-2" data-testid="copy-letter"><CopySimple size={16}/> Copier</button>
                <button onClick={() => { resolve({ group_id: letter.group_id }, "contested"); setLetter(null); }} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors" data-testid="mark-contested">
                  <PaperPlaneTilt size={16}/> Marquer contesté
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {batchLetters && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setBatchLetters(null)} data-testid="batch-modal">
          <div className="bg-background text-foreground hard-border max-w-4xl w-full my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="hard-border-b px-6 py-5 flex items-center justify-between">
              <div>
                <div className="ticker text-muted-foreground">Batch Claude · Top {batchLetters.letters.length} doublons</div>
                <div className="font-display font-black text-2xl mt-1">
                  {batchLetters.generated} courriels prêts <span className="text-base text-muted-foreground">· {fmt(batchLetters.total_potential)} couverts</span>
                </div>
              </div>
              <button onClick={() => setBatchLetters(null)} className="hover:text-[#D90429]" data-testid="close-batch"><X size={22}/></button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4" data-testid="batch-letters-list">
              {batchLetters.letters.map((lt, i) => (
                <details key={lt.group_id} className={`hard-border p-4 ${lt._sent ? "bg-[#2A9D8F]/10" : ""}`} open={i === 0}>
                  <summary className="cursor-pointer flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="ticker bg-[#D90429] text-white px-2 py-0.5">#{i + 1}</span>
                      <span className="font-display font-bold">{lt.vendor}</span>
                      <span className="ticker text-muted-foreground">{lt.date}</span>
                      <span className="font-mono font-bold text-[#D90429]">{fmt(lt.potential_recovery)}</span>
                      {lt._sent && <span className="ticker bg-[#2A9D8F] text-white px-2 py-0.5">ENVOYÉ → {lt._sent_to}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => { e.preventDefault(); copyBatchLetter(lt); }} className="ticker hover:text-[#D90429] flex items-center gap-1.5" data-testid={`batch-copy-${i}`}>
                        <CopySimple size={14}/> Copier
                      </button>
                      <button onClick={(e) => { e.preventDefault(); resolve({ group_id: lt.group_id }, "contested"); }} className="ticker hover:text-[#2A9D8F] flex items-center gap-1.5" data-testid={`batch-mark-${i}`}>
                        <Check size={14}/> Marquer
                      </button>
                    </div>
                  </summary>
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="ticker text-muted-foreground mb-1">Objet</div>
                    <div className="font-semibold mb-3">{lt.subject}</div>
                    <div className="ticker text-muted-foreground mb-1">Corps du courriel</div>
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed bg-secondary/30 p-3 hard-border mb-4">{lt.body}</pre>
                    {!lt._sent && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="ticker text-muted-foreground">Envoyer à :</label>
                        <input
                          type="email"
                          defaultValue={lt.vendor_email || ""}
                          onChange={(e) => setEmailDraft(prev => ({ ...prev, [lt.group_id]: e.target.value }))}
                          placeholder={`facturation@${lt.vendor.toLowerCase().replace(/\s+/g, "")}.com`}
                          className="hard-border px-3 py-2 flex-1 min-w-[240px] font-mono text-xs"
                          data-testid={`email-input-${i}`}
                        />
                        <button
                          onClick={() => sendLetterViaGmail(lt, i)}
                          disabled={sendingIdx === i || !gmailStatus.connected}
                          className="bg-[#2A9D8F] hover:bg-[#228277] disabled:opacity-50 text-white px-4 py-2 ticker flex items-center gap-2 transition-colors"
                          title={!gmailStatus.connected ? "Gmail pas connecté — vas dans Intégrations" : "Envoyer via Gmail"}
                          data-testid={`send-gmail-${i}`}
                        >
                          <EnvelopeSimple size={14}/> {sendingIdx === i ? "Envoi…" : "Envoyer via Gmail"}
                        </button>
                      </div>
                    )}
                  </div>
                </details>
              ))}
              {batchLetters.errors && batchLetters.errors.length > 0 && (
                <div className="hard-border border-[#D90429] p-4 bg-[#FFF4F4]">
                  <div className="ticker text-[#D90429] mb-2">Erreurs ({batchLetters.errors.length})</div>
                  <ul className="text-sm space-y-1">
                    {batchLetters.errors.map(e => <li key={e.group_id}>• {e.vendor}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="hard-border-t p-5 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                {gmailStatus.connected ? (
                  <><Check size={14} className="text-[#2A9D8F]"/> Gmail connecté → envoi direct possible</>
                ) : (
                  <><X size={14} className="text-[#D90429]"/> Gmail pas connecté → copie seulement</>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button onClick={copyAllBatchLetters} className="hard-border px-4 py-2.5 ticker hover:bg-foreground hover:text-white transition-colors flex items-center gap-2" data-testid="copy-all-batch">
                  <CopySimple size={16}/> Copier tout ({batchLetters.letters.length})
                </button>
                <button
                  onClick={sendAllBatchViaGmail}
                  disabled={!gmailStatus.connected}
                  className="bg-[#2A9D8F] hover:bg-[#228277] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors"
                  title={!gmailStatus.connected ? "Gmail pas connecté" : "Envoyer en masse"}
                  data-testid="send-all-batch"
                >
                  <EnvelopeSimple size={16}/> Envoyer tous via Gmail
                </button>
                <button onClick={() => setBatchLetters(null)} className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
